import React, { type JSX } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AppBar from '@mui/material/AppBar';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Slide from '@mui/material/Slide';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import { useState, useCallback, useEffect, useRef } from 'react';

import { AIAvatar } from 'components/AIComponents/AIAvatar';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import {
  deleteAIConversation,
  loadNextConversationsPage,
  fetchInitialConversations,
  setCurrentConversationId,
} from 'app-components/flux/ai-chat';
import type { ConversationSummary } from 'app-components/flux/ai-chat/aiChatTypes';
import * as aiChatSelectors from 'app-components/flux/ai-chat/aiChatSelectors';
import { getLogger } from 'app-components/utils/core';
import { makeStyles } from 'tss-react/mui';
import { tracker } from 'app-components/utils/core';
import { trackingEvents } from 'containers/SimplifiAiAssistant/AiAssistant/aiEvents';
import SquarePenIcon from 'assets/icons/square-pen.inline.svg';
import PanelLeftCloseIcon from 'assets/icons/panel-left-close.inline.svg';
import { useChatSession } from 'components/ChatLauncher/chatSession';
import type { ChatViewMode } from 'components/ChatLauncher/ChatDockContext';
import { CHAT_SIDEBAR_WIDTH } from '../fullscreenChatLayout';

const log = getLogger('ChatSideBar');
const HISTORY_LOAD_MORE_FOOTER_HEIGHT = 40;
const HISTORY_SKELETON_ROW_COUNT = 6;
const HISTORY_LOAD_MORE_SKELETON_ROW_COUNT = 2;
const HISTORY_SKELETON_WIDTHS = ['88%', '74%', '82%', '68%', '80%', '72%'] as const;

type HistoryListSkeletonProps = {
  rows?: number;
  'aria-label'?: string;
};

const HistoryListSkeleton = ({
  rows = HISTORY_SKELETON_ROW_COUNT,
  'aria-label': ariaLabel = 'Loading conversation history',
}: HistoryListSkeletonProps): JSX.Element => (
  <>
    {Array.from({ length: rows }, (_, index) => (
      <ListItem
        key={`history-skeleton-${index}`}
        dense
        disablePadding
        sx={{ paddingX: 1.5, paddingY: 0.25 }}
        aria-busy={index === 0 ? true : undefined}
        aria-label={index === 0 ? ariaLabel : undefined}
        aria-hidden={index > 0 ? true : undefined}
      >
        <Skeleton
          variant='rounded'
          animation='wave'
          sx={{
            height: 20,
            width: HISTORY_SKELETON_WIDTHS[index % HISTORY_SKELETON_WIDTHS.length],
            borderRadius: 1,
          }}
        />
      </ListItem>
    ))}
  </>
);

export const CHAT_SIDEBAR_MAX_WIDTH = CHAT_SIDEBAR_WIDTH;
export const CHAT_SIDEBAR_TRANSITION_MS = 200;

const slideTransition = {
  enter: CHAT_SIDEBAR_TRANSITION_MS,
  exit: CHAT_SIDEBAR_TRANSITION_MS,
};

const useStyles = makeStyles<{ open: boolean }>({ name: 'ai-chat-sidebar' })((theme, { open }) => ({
  overlayAppBar: {
    width: '60%',
    maxWidth: CHAT_SIDEBAR_MAX_WIDTH,
    height: '100%',
    left: 0,
    top: 0,
    boxShadow: theme.shadows[24],
    background: `linear-gradient(135deg, ${theme.palette.backdrop.light} 0%, ${theme.palette.primary.light}90 100%)`,
    borderRight: `1px solid ${theme.palette.border.light}`,
    backdropFilter: 'blur(16px)',
    zIndex: theme.zIndex.modal + 1,
    display: 'flex',
    flexDirection: 'column',
  },
  persistentDrawer: {
    order: 0,
    width: open ? CHAT_SIDEBAR_MAX_WIDTH : 0,
    maxWidth: CHAT_SIDEBAR_MAX_WIDTH,
    flexShrink: 0,
    height: '100%',
    boxShadow: 'none',
    background: `linear-gradient(135deg, ${theme.palette.backdrop.light} 0%, ${theme.palette.primary.light}90 100%)`,
    borderRight: open ? `1px solid ${theme.palette.border.light}` : 'none',
    backdropFilter: 'blur(16px)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    pointerEvents: open ? 'auto' : 'none',
    transition: theme.transitions.create(['width', 'border-color'], {
      duration: CHAT_SIDEBAR_TRANSITION_MS,
      easing: theme.transitions.easing.easeInOut,
    }),
  },
  persistentDrawerContent: {
    width: CHAT_SIDEBAR_MAX_WIDTH,
    minWidth: CHAT_SIDEBAR_MAX_WIDTH,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

type ChatSideBarProps = {
  onClose: () => void;
  open: boolean;
  persistOnNavigate?: boolean;
  viewMode?: ChatViewMode;
  persistent?: boolean;
  containedOverlay?: boolean;
};

const ChatSideBar = ({
  onClose,
  open,
  persistOnNavigate = false,
  viewMode = 'floating',
  persistent = false,
  containedOverlay = false,
}: ChatSideBarProps): JSX.Element | null => {
  const [selected, setSelected] = useState<string | number | null>(null);
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(true);
  const isFullscreen = viewMode === 'expanded';
  const isPersistentDrawer = persistent && isFullscreen;
  const isContainedOverlay = containedOverlay && isFullscreen && !isPersistentDrawer;
  const { classes } = useStyles({ open });
  const { startNewChat, conversationId: currentConversationId, loadConversation } = useChatSession();

  const isLoading = useAppSelector(aiChatSelectors.isLoadingConversations);
  const isFetchingInitial = useAppSelector(aiChatSelectors.isFetchingInitialConversations);
  const hasMore = useAppSelector(aiChatSelectors.hasMoreConversations);
  const conversationsPage = useAppSelector(aiChatSelectors.getConversationsPage);
  const conversationsError = useAppSelector(aiChatSelectors.getError);
  const chatHistory: ConversationSummary[] = useAppSelector(aiChatSelectors.getConversationsList);
  const dispatch = useAppDispatch();
  const historyListRef = useRef<HTMLUListElement | null>(null);
  const loadMoreSentinelRef = useRef<HTMLLIElement | null>(null);
  const isLoadingMoreRef = useRef(false);
  const wasLoadingConversationsRef = useRef(false);
  const hasFetchedConversationsPage = conversationsPage >= 0;
  const isLoadingMore = isLoading && hasFetchedConversationsPage;

  const deleteConversation = useCallback(
    async (id: string) => {
      const isCurrent = currentConversationId === id;
      const deletedIndex = chatHistory.findIndex((conversation) => conversation.id === id);
      const remainingConversations = chatHistory.filter((conversation) => conversation.id !== id);
      const nextConversation =
        deletedIndex >= 0
          ? (remainingConversations[deletedIndex] ?? remainingConversations[deletedIndex - 1] ?? null)
          : (remainingConversations[0] ?? null);

      try {
        if (isCurrent) {
          if (nextConversation?.id) {
            setSelected(nextConversation.id);
            dispatch(setCurrentConversationId(nextConversation.id));
          } else {
            setSelected(null);
            await startNewChat();
          }
        }

        await dispatch(deleteAIConversation(id)).unwrap();

        if (isCurrent && nextConversation?.id) {
          await loadConversation(nextConversation.id);
        }
      } catch (error) {
        log.error('Failed to delete conversation', error);
      }
    },
    [chatHistory, currentConversationId, dispatch, startNewChat, loadConversation],
  );

  const handleNewConversation = async () => {
    tracker.track(trackingEvents.AIChatNew);
    tracker.track(trackingEvents.AIChatSaved);
    await startNewChat();
    setSelected(null);
    if (!persistOnNavigate) {
      onClose();
    }
  };

  useEffect(() => {
    if (open && conversationsPage < 0) {
      dispatch(fetchInitialConversations());
    }
  }, [open, conversationsPage, dispatch]);

  const handleRetryFetchHistory = useCallback(() => {
    dispatch(fetchInitialConversations());
  }, [dispatch]);

  const requestNextHistoryPage = useCallback(() => {
    if (!open || !isHistoryExpanded || !hasMore || isLoading || isLoadingMoreRef.current) {
      return;
    }

    isLoadingMoreRef.current = true;
    dispatch(loadNextConversationsPage());
  }, [open, isHistoryExpanded, hasMore, isLoading, dispatch]);

  useEffect(() => {
    const justFinishedLoading = wasLoadingConversationsRef.current && !isLoading;
    wasLoadingConversationsRef.current = isLoading;

    if (!isLoading) {
      isLoadingMoreRef.current = false;
    }

    if (!justFinishedLoading || !hasMore || !open || !isHistoryExpanded) {
      return;
    }

    const root = historyListRef.current;
    const sentinel = loadMoreSentinelRef.current;
    if (!root || !sentinel) {
      return;
    }

    const rootRect = root.getBoundingClientRect();
    const sentinelRect = sentinel.getBoundingClientRect();
    if (sentinelRect.top <= rootRect.bottom + 80) {
      requestNextHistoryPage();
    }
  }, [isLoading, hasMore, open, isHistoryExpanded, requestNextHistoryPage]);

  useEffect(() => {
    if (!open || !isHistoryExpanded || !hasMore) {
      return;
    }

    const root = historyListRef.current;
    const sentinel = loadMoreSentinelRef.current;
    if (!root || !sentinel) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          requestNextHistoryPage();
        }
      },
      { root, rootMargin: '80px 0px', threshold: 0 },
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [open, isHistoryExpanded, hasMore, chatHistory.length, requestNextHistoryPage]);

  const isLoadingInitialHistory = conversationsPage < 0 && isFetchingInitial;
  const showInitialHistorySkeleton = isLoadingInitialHistory && chatHistory.length === 0;
  const showInitialHistoryLoadingBanner = isLoadingInitialHistory && chatHistory.length > 0;
  const showHistoryFetchError = conversationsPage < 0 && Boolean(conversationsError) && !isFetchingInitial;
  const showEmptyHistoryState = hasFetchedConversationsPage && chatHistory.length === 0 && !isLoading;

  const sidebarContent = (
    <>
      <Box className={classes.headerContainer}>
        <Box className={classes.titleContainer}>
          <AIAvatar />
          <Typography variant='titleBold'>Quicken AI</Typography>
          <Chip label='Beta' variant='tonal' color='warning' size='small' />
        </Box>
        <Tooltip title='Close Sidebar'>
          <IconButton id='ai-chat-sidebar-close' variant='tonal' size='small' onClick={onClose}>
            <PanelLeftCloseIcon width={18} height={18} />
          </IconButton>
        </Tooltip>
      </Box>
      <Stack gap={1} padding={2}>
        <Button
          id='ai-chat-sidebar-new'
          onClick={handleNewConversation}
          fullWidth
          size='medium'
          variant='contained'
          startIcon={<SquarePenIcon width={18} height={18} />}
        >
          New Chat
        </Button>
      </Stack>

      <Stack gap={2} sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
            ...(isHistoryExpanded && { flexGrow: 1 }),
          }}
        >
          <ListItemButton
            id='ai-chat-sidebar-history-toggle'
            onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
            sx={{
              paddingX: 2,
              flexShrink: 0,
              flexGrow: 0,
              minHeight: 'auto',
              height: 'auto',
            }}
          >
            <ListItemText primary={<Typography variant='captionMedium'>Past conversations</Typography>} />
            {isHistoryExpanded ? (
              <ExpandLessIcon fontSize='small' id='ai-chat-sidebar-history-toggle-icon-less' />
            ) : (
              <ExpandMoreIcon fontSize='small' id='ai-chat-sidebar-history-toggle-icon-more' />
            )}
          </ListItemButton>
          {isHistoryExpanded && (
            <List
              id='ai-chat-sidebar-history-list'
              ref={historyListRef}
              sx={{
                flexGrow: 1,
                minHeight: 0,
                overflowY: 'auto',
                paddingTop: 0,
                paddingX: 1,
              }}
            >
              {showInitialHistorySkeleton ? <HistoryListSkeleton /> : null}
              {showInitialHistoryLoadingBanner ? (
                <ListItem
                  dense
                  disablePadding
                  role='status'
                  aria-live='polite'
                  aria-label='Loading conversation history'
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    paddingX: 1.5,
                    paddingY: 0.75,
                  }}
                >
                  <CircularProgress size={14} color='inherit' aria-hidden />
                  <Typography variant='captionRegular' color='text.secondary'>
                    Loading conversations...
                  </Typography>
                </ListItem>
              ) : null}
              {showHistoryFetchError ? (
                <ListItem
                  dense
                  disablePadding
                  role='alert'
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    paddingX: 1.5,
                    paddingY: 1,
                    gap: 1,
                  }}
                >
                  <Typography variant='captionRegular' color='error'>
                    {conversationsError ?? 'Failed to load conversation history'}
                  </Typography>
                  <Button
                    id='ai-chat-sidebar-history-retry'
                    size='small'
                    variant='text'
                    color='error'
                    onClick={handleRetryFetchHistory}
                  >
                    Retry
                  </Button>
                </ListItem>
              ) : null}
              {showEmptyHistoryState ? (
                <ListItem dense disablePadding sx={{ paddingX: 1.5, paddingY: 0.5 }}>
                  <ListItemText
                    primary='No past conversations yet'
                    slotProps={{
                      primary: { variant: 'captionRegular', color: 'text.secondary' },
                    }}
                  />
                </ListItem>
              ) : null}
              {chatHistory.map((item, index) => {
                const conversationDomId = String(item.id ?? `idx-${index}`);
                return (
                  <ListItem
                    key={item.id ?? index}
                    disablePadding
                    dense
                    secondaryAction={
                      <IconButton
                        id={`ai-chat-sidebar-delete-${conversationDomId}`}
                        size='small'
                        className='chat-delete-button'
                        onClick={async (event) => {
                          event.stopPropagation();
                          await deleteConversation(item.id);
                        }}
                      >
                        <DeleteRoundedIcon />
                      </IconButton>
                    }
                    sx={{
                      '& .chat-delete-button': {
                        opacity: 0,
                        pointerEvents: 'none',
                        transition: 'opacity 0.2s ease',
                      },
                      '&:hover .chat-delete-button': {
                        opacity: 1,
                        pointerEvents: 'auto',
                      },
                    }}
                  >
                    <ListItemButton
                      id={`ai-chat-sidebar-conversation-${conversationDomId}`}
                      dense
                      disableGutters
                      selected={selected === item.id}
                      onClick={async () => {
                        setSelected(item.id);
                        try {
                          await loadConversation(item.id);
                          if (!persistOnNavigate) {
                            onClose();
                          }
                        } catch (error) {
                          log.error('Failed to load conversation', error);
                        }
                      }}
                      sx={{
                        paddingX: 1.5,
                        paddingY: 0.25,
                        borderRadius: '8px',
                      }}
                    >
                      <ListItemText
                        primary={item?.title ? item.title : `New Chat`}
                        slotProps={{
                          primary: {
                            variant: 'paragraphRegular',
                            sx: { maxLines: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                          },
                          secondary: { variant: 'captionRegular' },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
              {isLoadingMore ? <HistoryListSkeleton rows={HISTORY_LOAD_MORE_SKELETON_ROW_COUNT} /> : null}
              {hasMore ? (
                <ListItem
                  ref={loadMoreSentinelRef}
                  id='ai-chat-sidebar-history-sentinel'
                  sx={{
                    minHeight: HISTORY_LOAD_MORE_FOOTER_HEIGHT,
                    flexShrink: 0,
                  }}
                />
              ) : null}
            </List>
          )}
        </Box>
      </Stack>
    </>
  );

  if (isPersistentDrawer) {
    return (
      <Box
        id='ai-chat-sidebar'
        component='nav'
        aria-label='Chat history'
        aria-hidden={!open}
        className={classes.persistentDrawer}
      >
        <Box className={classes.persistentDrawerContent}>{sidebarContent}</Box>
      </Box>
    );
  }

  return (
    <>
      <Backdrop
        open={open}
        onClick={onClose}
        transitionDuration={slideTransition}
        sx={{
          position: isContainedOverlay ? 'absolute' : 'fixed',
          inset: isContainedOverlay ? 0 : undefined,
          zIndex: (theme) => theme.zIndex.modal,
        }}
      />
      <Slide
        id='ai-chat-sidebar'
        in={open}
        direction='right'
        timeout={slideTransition}
        mountOnEnter={false}
        unmountOnExit={false}
      >
        <AppBar
          position={isContainedOverlay ? 'absolute' : 'fixed'}
          className={classes.overlayAppBar}
          aria-hidden={!open}
          sx={{ visibility: open ? 'visible' : 'hidden', pointerEvents: open ? 'auto' : 'none' }}
        >
          {sidebarContent}
        </AppBar>
      </Slide>
    </>
  );
};

export default ChatSideBar;
